import { apiClient } from '../../services/apiClient';
import type {
  ExclusiveActorsResponse,
  ExclusiveActor,
  CreateExclusiveActorRequest,
  UpdateExclusiveActorRequest,
} from './types';

const BASE_PATH = '/organization/actors';

/**
 * 専属AI俳優一覧を取得
 */
export async function listExclusiveActors(): Promise<ExclusiveActorsResponse> {
  const { data } = await apiClient.get<ExclusiveActorsResponse>(BASE_PATH);
  return data;
}

/**
 * 公開AI俳優一覧を取得（read-only用）
 */
export async function listPublicActors(): Promise<ExclusiveActorsResponse> {
  const { data } = await apiClient.get<ExclusiveActorsResponse>(`${BASE_PATH}/public`);
  return data;
}

/** 内部名の空き確認 (グローバル一意)。名前設定ステップの「次へ」で使用。 */
export async function checkExclusiveActorName(name: string): Promise<{ available: boolean }> {
  const { data } = await apiClient.get<{ available: boolean }>(`${BASE_PATH}/check-name`, {
    params: { name },
  });
  return data;
}

/**
 * 専属AI俳優を新規作成
 */
export async function createExclusiveActor(
  request: CreateExclusiveActorRequest
): Promise<{ actor: ExclusiveActor }> {
  const formData = new FormData();
  formData.append('character_sheet', request.character_sheet);
  formData.append('name', request.name);
  formData.append('display_name', request.display_name);
  // キャラクター (ethnicity='character') は性別未指定を許可するため、
  // 未指定なら送らない (BE 側は未送信を null 扱いする)。
  if (request.gender) {
    formData.append('gender', request.gender);
  }

  if (request.face_image) {
    formData.append('face_image', request.face_image);
  }
  if (request.display_name_en) {
    formData.append('display_name_en', request.display_name_en);
  }
  if (request.min_age !== undefined) {
    formData.append('min_age', String(request.min_age));
  }
  if (request.max_age !== undefined) {
    formData.append('max_age', String(request.max_age));
  }
  if (request.ethnicity) {
    formData.append('ethnicity', request.ethnicity);
  }
  if (request.tags) {
    formData.append('tags', JSON.stringify(request.tags));
  }
  if (request.body_type) {
    formData.append('body_type', request.body_type);
  }
  if (request.physical_features) {
    formData.append('physical_features', request.physical_features);
  }
  if (request.character_traits) {
    formData.append('character_traits', request.character_traits);
  }
  if (request.generation_prompt) {
    formData.append('generation_prompt', request.generation_prompt);
  }
  if (request.source_photo_id) {
    formData.append('source_photo_id', request.source_photo_id);
  }

  const { data } = await apiClient.post<{ actor: ExclusiveActor }>(BASE_PATH, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return data;
}

/**
 * 専属AI俳優を更新
 */
export async function updateExclusiveActor(
  actorId: string,
  request: UpdateExclusiveActorRequest
): Promise<{ actor: ExclusiveActor }> {
  // ファイルがある場合はFormDataを使用
  if (request.character_sheet) {
    const formData = new FormData();
    formData.append('character_sheet', request.character_sheet);

    if (request.display_name !== undefined) {
      formData.append('display_name', request.display_name);
    }
    if (request.gender !== undefined) {
      formData.append('gender', request.gender ?? '');
    }
    if (request.min_age !== undefined) {
      formData.append('min_age', request.min_age === null ? '' : String(request.min_age));
    }
    if (request.max_age !== undefined) {
      formData.append('max_age', request.max_age === null ? '' : String(request.max_age));
    }
    if (request.ethnicity !== undefined) {
      formData.append('ethnicity', request.ethnicity || '');
    }
    if (request.character_traits !== undefined) {
      formData.append('character_traits', request.character_traits ?? '');
    }
    if (request.tags !== undefined) {
      formData.append('tags', JSON.stringify(request.tags));
    }
    if (request.display_order !== undefined) {
      formData.append('display_order', String(request.display_order));
    }

    const { data } = await apiClient.patch<{ actor: ExclusiveActor }>(
      `${BASE_PATH}/${actorId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return data;
  }

  // ファイルがない場合はJSONを使用
  const { data } = await apiClient.patch<{ actor: ExclusiveActor }>(
    `${BASE_PATH}/${actorId}`,
    request
  );
  return data;
}

/**
 * 専属AI俳優を削除
 */
export async function deleteExclusiveActor(actorId: string): Promise<void> {
  await apiClient.delete(`${BASE_PATH}/${actorId}`);
}

/**
 * 専属AI俳優の表示順を一括更新
 */
export async function reorderExclusiveActors(
  order: { id: string; display_order: number }[]
): Promise<void> {
  await apiClient.post(`${BASE_PATH}/reorder`, { order });
}

/**
 * キャラクター俳優 (ethnicity='character') の顔検査 (face-safe) をユーザー起点で
 * (再) 実行する (自組織の専属俳優のみ)。
 *
 * platform admin 専用の全状態再検査エンドポイントとは異なり、null(未検査)/error/
 * 永久pending のみ許可される。safe は 200 で {status: 'already_safe'} を返し、
 * restricted は 403 になる (「判定ガチャ」防止。再検査はプラットフォーム管理者のみ)。
 * 機能フラグ (FACE_SCAN_ENABLED) が off の場合は 409 が返る。
 */
export async function triggerExclusiveActorFaceScan(
  actorId: string
): Promise<{ status: string; job_id?: string | null }> {
  const { data } = await apiClient.post<{ status: string; job_id?: string | null }>(
    `${BASE_PATH}/${actorId}/face-scan`,
  );
  return data;
}

// === 専属AI俳優生成関連API ===

export type GeneratePersonRequest = {
  prompt: string;
  model?: string;
  gender?: 'male' | 'female';
  age_min?: number;
  age_max?: number;
  ethnicity?: string;
  body_type?: string;
  physical_features?: string;
};

export type GeneratePersonResponse = {
  image_base64: string;
  mime_type: string;
  prompt_used: string;
  /** 生成画像の素材 ID。シート生成・名前提案はこれを参照で渡す (旧バックエンドでは null) */
  material_id: string | null;
};

/** 元画像は source_material_id (人物生成の結果素材) か source_image_base64 (アップロード画像) のどちらか */
export type GenerateCharacterSheetRequest = {
  source_material_id?: string;
  source_image_base64?: string;
  additional_prompt?: string;
  model?: string;
};

export type GenerateCharacterSheetResponse = {
  image_base64: string;
  mime_type: string;
  material_id: string | null;
};

export type SuggestNameAttributes = {
  gender?: 'male' | 'female';
  age_min?: number;
  age_max?: number;
  body_type?: string;
  physical_features?: string;
};

export type SuggestNameRequest = {
  /** 省略可。人物生成の結果素材を参照で渡す */
  image_material_id?: string;
  /** 省略可 (アップロード画像用)。省略時は generation_prompt / attributes からテキストのみで提案する */
  image_base64?: string;
  ethnicity?: string;
  generation_prompt?: string;
  attributes?: SuggestNameAttributes;
};

export type NameSuggestion = {
  display_name: string;
  display_name_en: string;
};

export type SuggestNameResponse = {
  name_suggestions: NameSuggestion[];
  tags: string[];
};

// 同期生成 API は廃止。生成はすべて下記の非同期ジョブ API (*Async) を使う。

// === 非同期ジョブAPI（スマホスリープ対応）===
// 注：専属AI俳優も共通のasync_generation_jobs APIを使用する
import {
  generatePersonAsync as generatePersonAsyncApi,
  generateCharacterSheetAsync as generateCharacterSheetAsyncApi,
  suggestNameAsync as suggestNameAsyncApi,
  type AsyncJob,
  type JobWaitOptions,
} from '../../services/asyncJobApi';

export type { AsyncJob, JobWaitOptions };

/**
 * 人物画像を生成（非同期ジョブ版・専属AI俳優用）
 * スマホスリープ時のネットワーク切断に対応
 */
export async function generatePersonAsync(
  request: GeneratePersonRequest,
  options?: { onProgress?: (job: AsyncJob) => void }
): Promise<GeneratePersonResponse> {
  const result = await generatePersonAsyncApi(request, options);
  return {
    image_base64: result.image_base64,
    mime_type: result.mime_type,
    prompt_used: result.prompt_used,
    material_id: result.material_id,
  };
}

/**
 * キャラクターシートを生成（非同期ジョブ版・専属AI俳優用）
 * スマホスリープ時のネットワーク切断に対応
 */
export async function generateCharacterSheetAsync(
  request: GenerateCharacterSheetRequest,
  options?: { onProgress?: (job: AsyncJob) => void }
): Promise<GenerateCharacterSheetResponse> {
  const result = await generateCharacterSheetAsyncApi(request, options);
  return {
    image_base64: result.image_base64,
    mime_type: result.mime_type,
    material_id: result.material_id,
  };
}

/**
 * 名前を提案（非同期ジョブ版・専属AI俳優用）
 * スマホスリープ時のネットワーク切断に対応
 */
export async function suggestNameAsync(
  request: SuggestNameRequest,
  options?: JobWaitOptions
): Promise<SuggestNameResponse> {
  const result = await suggestNameAsyncApi({
    image_material_id: request.image_material_id,
    image_base64: request.image_base64,
    ethnicity: request.ethnicity,
    generation_prompt: request.generation_prompt,
    attributes: request.attributes,
  }, options);

  // バックエンドからの戻り値をそのまま返す
  return {
    name_suggestions: result.name_suggestions.map((s) => ({
      display_name: s.display_name,
      display_name_en: s.display_name_en || '',
    })),
    tags: result.tags || [],
  };
}
